import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { useAlertes } from '../state/alertes';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { useMobile } from '../lib/ecran';
import { centimes, dateFr, deviseReference, isoJour, montant } from '../lib/format';
import { T } from '../i18n';
import type { Assurance, Vehicule } from '../api/types';

/**
 * Les polices d'assurance.
 *
 * La date d'expiration vivait deja sur le vehicule — c'est elle qui allume la
 * pastille du menu. Mais une date ne suffit pas le jour d'un accident : il faut
 * l'assureur, le numero de police, la FRANCHISE et le type de couverture, parce
 * que ce sont eux qui decident de ce que l'agence recupere reellement. Les deux
 * restent synchronises : renouveler ici met a jour l'alerte.
 */
const VIDE = {
  vehicle: '', assureur: '', numeroPolice: '', type: 'tous_risques',
  dateDebut: '', dateFin: '', prime: '', franchise: '', telephoneAssureur: '', agence: '',
};

const JOUR = 86400000;

export default function Assurances() {
  const { peut } = useAuth();
  const { recharger: rechargerAlertes } = useAlertes();
  const mobile = useMobile();
  const [liste, setListe] = useState<Assurance[] | null>(null);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [edite, setEdite] = useState<Assurance | null>(null);
  const [f, setF] = useState({ ...VIDE });
  const [erreur, setErreur] = useState('');
  const [msg, setMsg] = useState('');

  const charger = useCallback(() => {
    api.get<Assurance[]>('/operations/assurances').then(setListe).catch(() => setListe([]));
    api.get<Vehicule[]>('/vehicules').then(setVehicules).catch(() => {});
  }, []);
  useEffect(charger, [charger]);

  const restant = (a: Assurance) => Math.ceil((+new Date(a.dateFin) - Date.now()) / JOUR);

  function ouvrirCreation(vehicleId = '') {
    setEdite(null);
    setF({ ...VIDE, vehicle: vehicleId, dateDebut: isoJour(new Date()) });
    setErreur(''); setOuvert(true);
  }

  function ouvrirRenouvellement(a: Assurance) {
    const v = typeof a.vehicle === 'string' ? a.vehicle : a.vehicle._id;
    setEdite(null);
    // Renouveler, c'est reprendre la meme police un an plus tard : on prefile
    // tout sauf les dates, qui repartent de la fin de la couverture actuelle.
    const debut = new Date(a.dateFin);
    const fin = new Date(debut); fin.setFullYear(fin.getFullYear() + 1);
    setF({
      vehicle: v, assureur: a.assureur, numeroPolice: '', type: a.type,
      dateDebut: isoJour(debut), dateFin: isoJour(fin),
      prime: String(a.primeMinor / 100), franchise: String(a.franchiseMinor / 100),
      telephoneAssureur: a.telephoneAssureur ?? '', agence: a.agence ?? '',
    });
    setErreur(''); setOuvert(true);
  }

  async function sauver() {
    setErreur('');
    const corps = {
      vehicle: f.vehicle, assureur: f.assureur, numeroPolice: f.numeroPolice,
      type: f.type, dateDebut: f.dateDebut, dateFin: f.dateFin,
      primeMinor: centimes(Number(f.prime || 0)),
      franchiseMinor: centimes(Number(f.franchise || 0)),
      telephoneAssureur: f.telephoneAssureur || undefined,
      agence: f.agence || undefined,
    };
    try {
      if (edite) await api.patch(`/operations/assurances/${edite._id}`, corps);
      else await api.post('/operations/assurances', corps);
      setOuvert(false);
      setMsg(edite ? T.assurances.policeMaj : T.assurances.policeEnregistree);
      charger(); rechargerAlertes();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.assurances.enregistrementImpossible); }
  }

  if (liste === null) return <Squelette lignes={6} />;

  const sansAssurance = vehicules.filter((v) => !liste.some((a) => {
    const id = typeof a.vehicle === 'string' ? a.vehicle : a.vehicle?._id;
    return id === v._id;
  }));

  const ta = T.assurances;
  const COUVERTURE_LABEL = ta.couverture;
  const devise = deviseReference();
  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{ta.titre}</h1>
          <p>{ta.sousTitre}</p>
        </div>
        {peut('modifier.vehicule') && (
          <div className="actions">
            <button className="btn btn-p" onClick={() => ouvrirCreation()}>{ta.enregistrerPolice}</button>
          </div>
        )}
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      {sansAssurance.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="ko" titre={ta.sansPolice(sansAssurance.length)}>
            {sansAssurance.slice(0, 6).map((v) => v.immatriculation).join(', ')}
            {sansAssurance.length > 6 && '…'}
            {' '}{ta.sansPoliceTexte}
          </Bandeau>
        </div>
      )}

      <Carte pad={false}>
        {liste.length === 0 ? (
          <div className="carte-p">
            <Vide titre={ta.aucune}
                  action={peut('modifier.vehicule')
                    ? <button className="btn btn-p btn-l" onClick={() => ouvrirCreation()}>{ta.premiere}</button>
                    : undefined}>
              {ta.videTexte}
            </Vide>
          </div>
        ) : mobile ? (
          <div className="cartes">
            {liste.map((a) => {
              const v = a.vehicle as Vehicule;
              const j = restant(a);
              return (
                <button key={a._id} className="carte-ligne"
                        onClick={() => (peut('modifier.vehicule') ? ouvrirRenouvellement(a) : undefined)}>
                  <div className="cl-tete">
                    <span className="cl-titre">{v?.marque} {v?.modele}</span>
                    <Badge statut={j < 0 ? 'retard' : j <= 30 ? 'retour' : 'disponible'}
                           libelle={j < 0 ? ta.expireeDepuisCourt(-j) : ta.joursCourt(j)} />
                  </div>
                  <div className="cl-sous">{v?.immatriculation} · {a.assureur} · {COUVERTURE_LABEL[a.type] ?? a.type}</div>
                  <div className="cl-sous">{ta.police(a.numeroPolice, dateFr(a.dateFin))}</div>
                  <div className="cl-pied">{ta.franchiseDe(montant(a.franchiseMinor))}</div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{ta.col.vehicule}</th><th>{ta.col.assureur}</th><th>{ta.col.numero}</th><th>{ta.col.couverture}</th>
                  <th>{ta.col.fin}</th><th className="droite">{ta.col.prime}</th>
                  <th className="droite">{ta.col.franchise}</th><th />
                </tr>
              </thead>
              <tbody>
                {liste.map((a) => {
                  const v = a.vehicle as Vehicule;
                  const j = restant(a);
                  return (
                    <tr key={a._id}>
                      <td><b>{v?.marque} {v?.modele}</b><div className="mini sourdine">{v?.immatriculation}</div></td>
                      <td>{a.assureur}{a.agence && <div className="mini sourdine">{a.agence}</div>}</td>
                      <td className="mini">{a.numeroPolice}</td>
                      <td>{COUVERTURE_LABEL[a.type] ?? a.type}</td>
                      <td>
                        {dateFr(a.dateFin)}
                        <div className="mini" style={{ color: j < 0 ? 'var(--danger)' : j <= 30 ? 'var(--attention)' : 'var(--texte3)' }}>
                          {j < 0 ? ta.expireeDepuis(-j) : ta.dans(j)}
                        </div>
                      </td>
                      <td className="droite montant">{montant(a.primeMinor)}</td>
                      <td className="droite montant">{montant(a.franchiseMinor)}</td>
                      <td className="droite">
                        {peut('modifier.vehicule') && (
                          <button className="btn btn-s" onClick={() => ouvrirRenouvellement(a)}>{ta.renouveler}</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        {ta.note}
      </p>

      {ouvert && (
        <Modale titre={edite ? ta.modifierPolice : ta.policeAssurance} onFermer={() => setOuvert(false)}>
          <div style={{ display: 'grid', gap: 12, maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
            <Champ label={ta.vehicule}>
              <select value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value })}>
                <option value="">{ta.choisir}</option>
                {vehicules.map((v) => (
                  <option key={v._id} value={v._id}>{v.marque} {v.modele} · {v.immatriculation}</option>
                ))}
              </select>
            </Champ>
            <div className="grille g2">
              <Champ label={ta.assureur}><input value={f.assureur} onChange={(e) => setF({ ...f, assureur: e.target.value })} placeholder={ta.assureurExemple} /></Champ>
              <Champ label={ta.agence} aide={ta.facultatif}><input value={f.agence} onChange={(e) => setF({ ...f, agence: e.target.value })} /></Champ>
              <Champ label={ta.numero}><input value={f.numeroPolice} onChange={(e) => setF({ ...f, numeroPolice: e.target.value })} /></Champ>
              <Champ label={ta.couvertureLabel}>
                <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                  {Object.entries(COUVERTURE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </Champ>
              <Champ label={ta.debut}><input type="date" value={f.dateDebut} onChange={(e) => setF({ ...f, dateDebut: e.target.value })} /></Champ>
              <Champ label={ta.fin} aide={ta.finAide}>
                <input type="date" value={f.dateFin} onChange={(e) => setF({ ...f, dateFin: e.target.value })} />
              </Champ>
              <Champ label={ta.prime(devise)}><input type="number" inputMode="decimal" value={f.prime} onChange={(e) => setF({ ...f, prime: e.target.value })} /></Champ>
              <Champ label={ta.franchise(devise)} aide={ta.franchiseAide}>
                <input type="number" inputMode="decimal" value={f.franchise} onChange={(e) => setF({ ...f, franchise: e.target.value })} />
              </Champ>
              <Champ label={ta.telephone} aide={ta.telephoneAide}>
                <input value={f.telephoneAssureur} onChange={(e) => setF({ ...f, telephoneAssureur: e.target.value })} placeholder="0…" />
              </Champ>
            </div>
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setOuvert(false)}>{ta.annuler}</button>
            <button className="btn btn-p"
                    disabled={!f.vehicle || f.assureur.length < 2 || !f.numeroPolice || !f.dateDebut || !f.dateFin}
                    onClick={() => void sauver()}>{ta.enregistrer}</button>
          </div>
        </Modale>
      )}
    </>
  );
}
